import { useEffect, useState, useCallback } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import { githubAuthApiRef } from '../../apis';

interface GitHubIssue {
  id: number;
  title: string;
  html_url: string;
  state: string;
  repository: { full_name: string };
  labels: Array<{ name: string; color: string }>;
  updated_at: string;
  pull_request?: unknown;
}

const useStyles = makeStyles(theme => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(6),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  repoChip: {
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: '0.6rem',
  },
  labelChip: {
    marginLeft: theme.spacing(0.5),
    height: 16,
    fontSize: '0.55rem',
  },
  secondary: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  openIcon: {
    padding: theme.spacing(0.5),
  },
}));

const ago = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const MyIssuesCard = () => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const [issues, setIssues] = useState<GitHubIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const token = await githubAuth.getAccessToken(['repo']);
      const resp = await fetch(
        'https://api.github.com/issues?filter=assigned&state=open&sort=updated&per_page=20',
        {
          headers: {
            Authorization: `token ${token}`,
            Accept: 'application/vnd.github.v3+json',
          },
        },
      );
      if (!resp.ok) {
        throw new Error(`GitHub API returned ${resp.status}`);
      }
      const data: GitHubIssue[] = await resp.json();
      // Filter out pull requests (GitHub API includes them in /issues)
      setIssues(data.filter(i => !i.pull_request));
    } catch (e: any) {
      setError(e.message ?? 'Could not load issues');
    } finally {
      setLoading(false);
    }
  }, [githubAuth]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const content = () => {
    if (loading) {
      return (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      );
    }
    if (error) {
      return <Typography className={classes.empty}>{error}</Typography>;
    }
    if (issues.length === 0) {
      return (
        <Typography className={classes.empty}>
          No open issues assigned to you.
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {issues.slice(0, 8).map(issue => (
          <ListItem key={issue.id} className={classes.listItem}>
            <ListItemText
              primary={issue.title}
              secondary={
                <span className={classes.secondary}>
                  <Chip
                    label={issue.repository.full_name.split('/').pop()}
                    size="small"
                    variant="outlined"
                    className={classes.repoChip}
                  />
                  {issue.labels.slice(0, 2).map(l => (
                    <Chip
                      key={l.name}
                      label={l.name}
                      size="small"
                      className={classes.labelChip}
                      style={{
                        backgroundColor: `#${l.color}`,
                        color:
                          parseInt(l.color, 16) > 0x7fffff ? '#000' : '#fff',
                      }}
                    />
                  ))}
                  <Typography variant="caption" color="textSecondary">
                    {ago(issue.updated_at)}
                  </Typography>
                </span>
              }
            />
            <Tooltip title="Open in GitHub">
              <IconButton
                className={classes.openIcon}
                size="small"
                href={issue.html_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItem>
        ))}
        {issues.length > 8 && (
          <Typography
            variant="caption"
            style={{ padding: 8, display: 'block' }}
          >
            +{issues.length - 8} more
          </Typography>
        )}
      </List>
    );
  };

  return (
    <InfoCard
      title="My Issues"
      subheader={
        issues.length > 0
          ? `${issues.length} open issue${issues.length === 1 ? '' : 's'} assigned to you`
          : undefined
      }
      action={
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchIssues} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {content()}
    </InfoCard>
  );
};
